import {
  differenceInDays,
  endOfMonth,
  getMonth,
  getYear,
} from 'date-fns';
import { Linking } from 'react-native';
import { SelectedPaymentMethod } from 'hooks/useSelectedPaymentMethod';
import {
  AdyenWalletType,
  BillingItem,
  Card,
  CardStatus,
  CompanyBenefit,
  ExternalPaymentMethod,
  Maybe,
  ParkingPaymentState,
  PaymentMethodType,
  PaymentOption,
  PaymentType,
  Receipt,
} from 'types/generatedSchemaTypes';
import {
  getPaymentMethodUsedInSession,
  SessionWithPayment,
} from './ongoingSessionUtils';
import { showToast } from 'components/common/CustomToast.tsx';
import {
  getCorrectBillingItem,
  truncateText,
} from 'utils/commonUtils.ts';

export const PAYMENT_LOGO_DEFAULT = 'Service-Filled';

export const SERVICE_FEE_TYPES: string[] = [
  'SERVICE_FEE',
  'SERVICE_FEE_FIXED',
  'SERVICE_FEE_PERCENTAGE',
];

export const INCOMPLETE_PARKING_PAYMENT_STATES: ParkingPaymentState[] = [
  'NotInitiated',
  'Pending',
  'Terminated',
];

export function isCompletedPaymentState(
  state: ParkingPaymentState | null | undefined
): boolean {
  return (
    state != null && INCOMPLETE_PARKING_PAYMENT_STATES.includes(state) === false
  );
}

// The backend has not heard back from the external payment provider yet.
export const AWAITING_EXTERNAL_PAYMENT_STATES: ParkingPaymentState[] = [
  'NotInitiated',
  'Pending',
  'Retrying',
];

/**
 * After the customer returns from Swish, checks the parking start's payment
 * until the backend knows the outcome. On Cancel the return link can arrive
 * a few seconds before the backend does, so one check is not enough.
 * The limit is a deadline, not a count: one lookup can take several seconds
 * when Apollo's RetryLink retries on a bad network.
 * Only 'failed' changes what the app does; 'notFailed' (paid, or still unknown
 * at the deadline) keeps the existing completion path.
 */
export const waitForExternalPaymentResult = async (
  sessionId: string | null,
  readPaymentState: (
    sessionId: string
  ) => Promise<ParkingPaymentState | null | undefined>,
  hasLeft: () => boolean,
  { timeoutMs = 10000, intervalMs = 1000 } = {}
): Promise<'failed' | 'notFailed' | 'left' | 'noAttempt'> => {
  if (!sessionId) {
    return 'noAttempt';
  }
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    let state: ParkingPaymentState | null | undefined;
    try {
      state = await readPaymentState(sessionId);
    } catch {
      state = undefined;
    }
    if (hasLeft()) {
      return 'left';
    }
    if (state === 'Terminated' || state === 'Failed') {
      return 'failed';
    }
    if (state && !AWAITING_EXTERNAL_PAYMENT_STATES.includes(state)) {
      return 'notFailed';
    }
    if (Date.now() >= deadline) {
      return 'notFailed';
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    if (hasLeft()) {
      return 'left';
    }
  }
};

export function isServiceFeeType(type: string | null | undefined): boolean {
  return type != null && SERVICE_FEE_TYPES.includes(type);
}

const CARD_LOGO_DEFAULT = 'Credit_Card-Outlined';
const CARD_LOGO_VISA = 'Visa_Logo';
const CARD_LOGO_VISA_OUTLINED = 'Visa_Logo_Outlined';
const CARD_LOGO_MASTERCARD = 'MasterCard_Logo';
const PAYMENT_LOGO_SWISH = 'Swish';
const BUSINESS_LOGO_OUTLINED = 'Business-Outlined';
const SWISH_LOGO_OUTLINED = 'Swish_Outlined';
const SWISH_LOGO = 'Swish';
export const MOBILE_PAY_LOGO = 'Mobile-Pay';

const BLOCK_COMPANY_BENEFIT_USAGE_SCREENS = ['permitEdit', 'BUY_PERMIT'];

const BLOCK_EXTERNAL_PAYMENT_SCREENS = [
  'EDIT_PREPAID',
  'BUY_PERMIT',
  'CHARGE',
  'permitEdit',
  'prepaidEdit',
  'PARK_AND_CHARGE',
];

export const getCardLogo = (
  cardType?: string | null,
  useOutlined?: boolean
): string => {
  let cardLogo = CARD_LOGO_DEFAULT;
  if (cardType?.toLowerCase().includes('visa')) {
    cardLogo = useOutlined ? CARD_LOGO_VISA_OUTLINED : CARD_LOGO_VISA;
  } else if (cardType?.toLowerCase().includes('mastercard')) {
    cardLogo = CARD_LOGO_MASTERCARD;
  }
  return cardLogo;
};

export const getCardMaskedLastNums = (paymentCard: Maybe<Card>) =>
  paymentCard?.lastNums ? ` **** ${paymentCard?.lastNums}` : '';

export const blockPaymentUsage = (
  parkingTab?: string,
  route?: Maybe<string>,
  isExternalPayment?: boolean
) => {
  const screenArray = isExternalPayment
    ? BLOCK_EXTERNAL_PAYMENT_SCREENS
    : BLOCK_COMPANY_BENEFIT_USAGE_SCREENS;
  return (
    screenArray.includes(parkingTab ?? '') || screenArray.includes(route ?? '')
  );
};

type PaymentMethodLogo = {
  logoName: string;
  logoSize: string;
};

export const getPaymentMethodLogo = (
  paymentType: Maybe<PaymentType>,
  cardType?: Maybe<string>,
  useOutlined?: boolean
): PaymentMethodLogo | undefined => {
  switch (paymentType) {
    case 'CORPORATE_ACCOUNT':
      return {
        logoName: BUSINESS_LOGO_OUTLINED,
        logoSize: 'm',
      };
    case 'CREDIT_CARD':
      return {
        logoName: getCardLogo(cardType, useOutlined),
        logoSize: cardType?.toLowerCase().includes('visa') ? 'xl' : 'm',
      };
    case 'SWISH':
      return {
        logoName: useOutlined ? SWISH_LOGO_OUTLINED : SWISH_LOGO,
        logoSize: 'm',
      };
    case 'MOBILEPAY':
      return {
        logoName: MOBILE_PAY_LOGO,
        logoSize: 'm',
      };
    case 'PAY_AFTERWARDS':
      return {
        logoName: 'Credit_Card-Outlined',
        logoSize: 'm',
      };
    default:
      return undefined;
  }
};

export const getPaymentMethodText = (
  paymentType: Maybe<PaymentType>,
  t: (key: string) => string
) => {
  if (paymentType === 'MOBILEPAY') {
    return t('paymentMethods.mobilePay');
  }
  return '';
};

export const getPaymentMethodDetails = (
  session: SessionWithPayment,
  paymentCards: Maybe<Card[]> | undefined,
  companyBenefits: Maybe<CompanyBenefit[]> | undefined
) => {
  const {
    paymentMethodType,
    paymentMethodInfo,
    cardType,
    walletPaymentMethod,
  } = getPaymentMethodUsedInSession(session, paymentCards, companyBenefits);
  // A session paid with a stored wallet card shows the wallet brand mark,
  // never the underlying scheme logo.
  const isWalletFunded =
    walletPaymentMethod === 'APPLE_PAY' || walletPaymentMethod === 'GOOGLE_PAY';
  const paymentMethodLogo = isWalletFunded
    ? {
        logoName: getWalletPaymentMethodIcon(walletPaymentMethod),
        logoSize: 'm',
      }
    : getPaymentMethodLogo(paymentMethodType as PaymentType, cardType, true);

  return { paymentMethodType, paymentMethodInfo, cardType, paymentMethodLogo };
};

export const cardErrorStatuses = [
  'Blocked',
  'Expired',
  'Terminated',
  'Unavailable',
] as CardStatus[];

export const isCardErrorStatus = (cardStatus: CardStatus) => {
  return cardErrorStatuses.includes(cardStatus);
};

// Only an `Active` card is selectable for payment. Transient statuses such as
// `Adding` (Adyen mints a pending card per init before webhook activation) and
// `Verifying` (Nets, while its readCard poll has not yet reached Active) are
// surfaced to the user as in-progress rows but must not be selectable — they
// cannot be charged. Error statuses are likewise non-selectable.
export const isCardSelectable = (cardStatus?: Maybe<CardStatus>): boolean =>
  (cardStatus ?? 'Active') === 'Active';

// A non-Active card is a pending row. Abandoned/retried Adyen card-add flows can
// accumulate several `Adding` cards; collapse them to a single pending row so the
// selection list shows one in-progress entry rather than a pile of duplicates.
// Active cards are always kept (each is a distinct, chargeable card).
export const dedupePendingCards = <T extends { status?: Maybe<CardStatus> }>(
  cards: T[]
): T[] => {
  let pendingKept = false;
  return cards.filter((card) => {
    if (isCardSelectable(card.status)) {
      return true;
    }
    if (pendingKept) {
      return false;
    }
    pendingKept = true;
    return true;
  });
};

export const getExpiringSoonCards = (cards: Card[]) => {
  return cards.filter((card) =>
    isCardExpiresSoon(
      getDaysUntilExpiration(
        card.status as CardStatus,
        card.expMonth,
        card.expYear
      )
    )
  );
};

export const getAmountOfExpiresSoonCards = (cards: Card[]) => {
  return getExpiringSoonCards(cards).length;
};

const getExpiredCards = (cards: Card[]) => {
  return cards.filter((card) => card.status === 'Expired');
};

export const getAmountOfExpiredCards = (cards: Card[]) => {
  return getExpiredCards(cards).length;
};

export const isCardExpiresSoon = (daysUntilExpiration: number) => {
  return daysUntilExpiration >= 0;
};

export const getDaysUntilExpiration = (
  cardStatus: CardStatus,
  expMonth?: string | null,
  expYear?: string | null
) => {
  let daysUntilExpiration = -1;
  const isErrorStatus = isCardErrorStatus(cardStatus);
  const dateNow = new Date();

  if (
    !!expMonth &&
    !!expYear &&
    getYear(dateNow) === parseInt(expYear, 10) &&
    getMonth(dateNow) + 1 === parseInt(expMonth, 10) &&
    !isErrorStatus
  ) {
    daysUntilExpiration = differenceInDays(endOfMonth(dateNow), dateNow);
  }
  return daysUntilExpiration;
};

// MM/YY, matching the card face. Some cards come without an expiry date —
// return '' so callers can hide the line instead of formatting an invalid date.
export const getCardExpiryDateText = (
  expMonth?: string | null,
  expYear?: string | null
) =>
  expMonth && expYear
    ? `${expMonth.padStart(2, '0')}/${expYear.slice(-2)}`
    : '';

export const getExternalPaymentLogo = (
  externalPayment?: ExternalPaymentMethod
) => {
  let paymentLogo = PAYMENT_LOGO_DEFAULT;
  if (externalPayment) {
    if (externalPayment.toLocaleLowerCase().includes('swish')) {
      paymentLogo = PAYMENT_LOGO_SWISH;
    }
  }
  return paymentLogo;
};

export const isCard = (item: any): item is Card => {
  return item && item.cardId !== undefined;
};

export const isCompanyBenefit = (item: any): item is CompanyBenefit => {
  return item && item.benefitTargetUid !== undefined;
};

export const isCorporateAccountPaymentOption = (item: any): boolean => {
  return (
    item && item.optionId !== undefined && item.method === 'CORPORATE_ACCOUNT'
  );
};

export const isExternalPayment = (item: any): item is ExternalPaymentMethod => {
  return item && item.externalPaymentType !== undefined;
};

export const isWalletToken = (item: any): boolean =>
  item?.method === 'MOBILEPAY';

// Generic fallback icon for a stored Adyen wallet whose method is absent or
// unrecognized; known wallet types get their brand mark below.
export const WALLET_ICON = 'Payment-Card';

export type AdyenWalletRowDescriptor = {
  labelKey: string;
  icon: string;
  testID: string;
};

// Single source for every surface that offers an Adyen wallet for adding.
export const ADYEN_WALLET_ROWS: Record<
  AdyenWalletType,
  AdyenWalletRowDescriptor
> = {
  APPLE_PAY: {
    labelKey: 'payment.applePay',
    icon: 'ApplePay_Logo',
    testID: 'add-apple-pay-button',
  },
  GOOGLE_PAY: {
    labelKey: 'payment.googlePay',
    icon: 'GooglePay_Logo',
    testID: 'add-google-pay-button',
  },
};

// Brand mark for a stored Adyen wallet's row/card (AimoUI icon-map keys).
export const getWalletPaymentMethodIcon = (
  paymentMethod: Maybe<PaymentMethodType> | undefined
): string =>
  isAdyenWalletPaymentMethod(paymentMethod)
    ? ADYEN_WALLET_ROWS[paymentMethod].icon
    : WALLET_ICON;

/**
 * True for Adyen stored-wallet methods (Apple Pay / Google Pay).
 * MobilePay is a separate Nets PaymentOption and is not included.
 */
export const isAdyenWalletPaymentMethod = (
  paymentMethod: Maybe<PaymentMethodType> | undefined
): paymentMethod is AdyenWalletType =>
  paymentMethod === 'APPLE_PAY' || paymentMethod === 'GOOGLE_PAY';

// True when `item` is a Card whose funding source is a stored Adyen wallet
// (Apple Pay/Google Pay), as opposed to a plain tokenized card or the legacy
// MobilePay PaymentOption isWalletToken() checks above. Absent/legacy cards
// (paymentMethod null, predating the field) are NOT wallet cards.
export const isWalletCard = (item: Maybe<Card> | undefined): boolean =>
  isAdyenWalletPaymentMethod(item?.paymentMethod);

// The i18n key for a wallet method's display label (row title / card title),
// or undefined for a non-wallet method.
export const getWalletPaymentMethodLabelKey = (
  paymentMethod: Maybe<PaymentMethodType> | undefined
): string | undefined =>
  isAdyenWalletPaymentMethod(paymentMethod)
    ? ADYEN_WALLET_ROWS[paymentMethod].labelKey
    : undefined;

export const resolveAddCardScreenTitle = (
  t: (key: string) => string,
  cardStatus: string,
  walletType: Maybe<PaymentMethodType> | undefined
): string => {
  if (cardStatus === 'Verifying') {
    return t('paymentMethods.verifying');
  }
  return t(
    getWalletPaymentMethodLabelKey(walletType) ?? 'payment.addPaymentCard'
  );
};

export type WalletRowVisibilityInput = {
  // useAdyenCardAddEnabled() — wallets are Adyen-only, no new flag.
  cardAddEnabled: boolean;
  // useWalletCapability()'s flag for this specific walletType.
  capable: boolean;
};

// The "Add Apple Pay"/"Add Google Pay" row visibility: visible iff the
// card-add flag is on and the platform reports capability for this wallet.
// An already-stored occupying wallet (Active/Verifying/Blocked/fresh Adding)
// does not hide the row — the add sheet still lists it and shows a
// non-selectable "Added" state.
export const shouldShowWalletAddRow = ({
  cardAddEnabled,
  capable,
}: WalletRowVisibilityInput): boolean => cardAddEnabled && capable;

// Badge copy for an occupying wallet row. Empty remote values for this key
// fall back to English so the outlined badge is never blank.
export const resolvePaymentMethodsAddedLabel = (
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const label = t('paymentMethods.added');
  if (label?.trim()) {
    return label;
  }
  const fallback = t('paymentMethods.added', { lng: 'en' });
  return fallback?.trim() ? fallback : 'Added';
};

const ADYEN_WALLET_OCCUPYING_STATUSES: ReadonlySet<string> = new Set([
  'Active',
  'Verifying',
  'Blocked',
  'Adding',
]);

// True when the customer already has an Adyen stored wallet of `walletType`
// occupying the one-per-type slot: Active, Verifying, Blocked or Adding.
// Blocked occupies because the service can reactivate blocked cards. Adding
// occupies at any age: an abandoned add is discarded server-side (cancel on
// abandon + reaper), never reclaimed by a client-side clock. Note:
// Customer.paymentCards currently filters ADDING out, so the Adding branch
// only fires when the card list source includes those stubs; process
// occupancy still uses the local wallet-slot marker. Used by
// AddOtherPaymentMethod for "Added".
export const hasActiveAdyenWalletOfType = (
  paymentCards: Maybe<Maybe<Card>[]> | undefined,
  walletType: PaymentMethodType
): boolean =>
  !!paymentCards?.some(
    (card) =>
      card?.paymentMethod === walletType &&
      !!card.status &&
      ADYEN_WALLET_OCCUPYING_STATUSES.has(card.status)
  );

// Menu-row presentation for a stored wallet card (account menu's default-card
// row): the wallet brand mark + the customer's custom name (truncated like the
// plain-card subtitle), wallet label fallback. Returns undefined for anything
// that is not a wallet card so callers keep their existing plain-card logic.
export const getWalletCardMenuPresentation = (
  card: Maybe<Card> | undefined,
  t: (key: string) => string
): { iconName: string; subtitle: string } | undefined => {
  if (!isWalletCard(card)) {
    return undefined;
  }
  const labelKey = getWalletPaymentMethodLabelKey(card?.paymentMethod);
  return {
    iconName: getWalletPaymentMethodIcon(card?.paymentMethod),
    subtitle:
      truncateText(card?.description ?? '', 15) ||
      (labelKey ? t(labelKey) : ''),
  };
};

export const determinePaymentType = (
  paymentMethod: SelectedPaymentMethod
): PaymentType | undefined => {
  if (paymentMethod.cardId) {
    return 'CREDIT_CARD';
  } else if (paymentMethod.benefitTargetUid) {
    return 'CORPORATE_ACCOUNT';
  } else if (paymentMethod.externalPaymentType) {
    return paymentMethod.externalPaymentType as PaymentType;
  } else if (paymentMethod.method === 'MOBILEPAY') {
    return 'MOBILEPAY' as PaymentType;
  }
  return undefined;
};

export const getChargingSessionPaymentType = (
  cardId?: Maybe<string>,
  corporateAccountUid?: Maybe<string>,
  paymentType?: Maybe<PaymentType>
): Maybe<PaymentType> => {
  if (paymentType) {
    return paymentType;
  }

  if (corporateAccountUid) {
    return 'CORPORATE_ACCOUNT';
  }

  if (cardId) {
    return 'CREDIT_CARD';
  }
  return null;
};

/**
 * Card data needed to generate accessibility label
 */
export interface CardAccessibilityData {
  /** Card description or type */
  cardDescription: string;
  /** Last digits of the card number */
  lastDigits: string;
  /** Whether the card is default */
  isDefault: boolean;
  /** Whether the card has an error status */
  isErrorStatus: boolean;
  /** Whether the card is expiring soon */
  expiresSoon: boolean;
  /** Whether the card is being verified */
  isVerifying: boolean;
  /** Whether it's a business card */
  isBusiness: boolean;
}

/**
 * Generates an accessibility label for a payment card
 * @param cardData Object containing card data needed for accessibility label
 * @param t Translation function
 * @returns Formatted accessibility label string
 */
export const getCardAccessibilityLabel = (
  cardData: CardAccessibilityData,
  t: (key: string) => string
): string => {
  const {
    cardDescription,
    lastDigits,
    isDefault,
    isErrorStatus,
    expiresSoon,
    isVerifying,
    isBusiness,
  } = cardData;

  // Build status text based on card state
  let statusText = '';
  if (isErrorStatus) {
    statusText = t('ariaLabels.paymentMethods.cardError');
  } else if (expiresSoon) {
    statusText = t('ariaLabels.paymentMethods.expiringCard');
  } else if (isVerifying) {
    statusText = t('ariaLabels.paymentMethods.verifying');
  } else if (isBusiness) {
    statusText = t('ariaLabels.paymentMethods.business');
  }

  const defaultText = isDefault ? t('ariaLabels.paymentMethods.default') : '';

  const cardPart = cardDescription;
  const numberPart = `${t('ariaLabels.paymentMethods.cardNumber')} ${lastDigits}`;
  const defaultPart = defaultText ? `, ${defaultText}` : '';
  const statusPart = statusText ? `, ${statusText}` : '';

  return `${cardPart}, ${numberPart}${defaultPart}${statusPart}`;
};

/**
 * Enriched PaymentOption type with optionName
 */
export type EnrichedPaymentOption = PaymentOption & {
  optionName?: string;
};

/**
 * Enriches payment options with memberGroup name (if available), resolved from companyBenefits
 * @param paymentOptions Array of payment options to enrich
 * @param companyBenefits Array of company benefits to match against
 * @returns Array of enriched payment options with optionName property
 */
export const enrichPaymentOptionsWithNames = (
  paymentOptions: Maybe<PaymentOption[]> | undefined,
  companyBenefits: Maybe<CompanyBenefit[]> | undefined
): EnrichedPaymentOption[] => {
  if (!paymentOptions) {
    return [];
  }

  return paymentOptions
    .map((option) => enrichPaymentOptionWithName(option, companyBenefits))
    .filter(Boolean) as EnrichedPaymentOption[];
};

/**
 * Enriches a single payment option with memberGroup name (if available), resolved from companyBenefits
 * @param paymentOption Payment option to enrich
 * @param companyBenefits Array of company benefits to match against
 * @returns Enriched payment option with optionName property, or null if input is null
 */
export const enrichPaymentOptionWithName = (
  paymentOption: Maybe<PaymentOption> | null,
  companyBenefits: Maybe<CompanyBenefit[]> | undefined
): EnrichedPaymentOption | null => {
  if (!paymentOption) {
    return null;
  }

  let optionName = paymentOption.optionId; // By default, show optionId

  // Prefer the company name when available
  if (paymentOption.name) {
    optionName = paymentOption.name;
  } else {
    // Fallback: resolve the name from companyBenefits (member-group CAs)
    const matchingBenefit = companyBenefits?.find(
      (benefit) => benefit?.benefitTargetUid === paymentOption.optionId
    );
    if (matchingBenefit?.name) {
      optionName = matchingBenefit.name;
    }
  }

  return {
    ...paymentOption,
    optionName,
  };
};

/**
 * Checks if a payment option is valid for the selected vehicle based on registration plates
 * @param paymentOption - The payment option to validate
 * @param selectedVehicle - The currently selected vehicle
 * @returns true if the payment option is valid for the vehicle, false otherwise
 */
export const isPaymentOptionValidForVehicle = (
  paymentOption: PaymentOption,
  selectedVehicle?: { licensePlate?: { plateText?: string } } | null
): boolean => {
  // If no registration plates specified, payment option is valid for any vehicle
  if (
    !paymentOption.registrationPlates ||
    paymentOption.registrationPlates.length === 0
  ) {
    return true;
  }

  // If no vehicle selected, payment option requiring specific plates is invalid
  if (!selectedVehicle?.licensePlate?.plateText) {
    return false;
  }

  // Check if selected vehicle's plate matches any of the payment option's plates
  return paymentOption.registrationPlates.some(
    (plate) => plate?.plateText === selectedVehicle.licensePlate?.plateText
  );
};

export const getPaymentMethodInfo = (
  isCreditCard: string | boolean,
  paymentMethod: SelectedPaymentMethod,
  parkingZoneCountryCode?: string,
  t?: (key: string) => string
): { text: string | undefined; icon: string | undefined } => {
  let text: string | undefined;
  let icon: string | undefined;

  if (isCreditCard) {
    if (isWalletCard(paymentMethod)) {
      // Stored wallet card: custom name first, wallet label fallback; always
      // the wallet brand mark, never the underlying scheme logo.
      const walletLabelKey = getWalletPaymentMethodLabelKey(
        paymentMethod?.paymentMethod
      );
      text =
        paymentMethod?.description ||
        (t && walletLabelKey ? t(walletLabelKey) : undefined);
      icon = getWalletPaymentMethodIcon(paymentMethod?.paymentMethod);
    } else {
      text =
        (paymentMethod?.description || paymentMethod?.lastNums) ?? undefined;
      icon = getCardLogo(paymentMethod?.type);
    }
  } else if (
    paymentMethod?.benefitTargetUid &&
    paymentMethod.countryCode === parkingZoneCountryCode
  ) {
    text = paymentMethod?.name;
    icon = PAYMENT_LOGO_DEFAULT;
  } else if (paymentMethod?.externalPaymentType) {
    text = paymentMethod?.name;
    icon = getExternalPaymentLogo(paymentMethod?.externalPaymentType);
  } else if (isCorporateAccountPaymentOption(paymentMethod)) {
    // Handle corporate account payment options
    text = (paymentMethod as any)?.optionName;
    icon = 'Business-Filled';
  } else if (paymentMethod?.method === 'MOBILEPAY') {
    text = t?.('paymentMethods.mobilePay');
    icon = MOBILE_PAY_LOGO;
  }

  return { text, icon };
};

export const getPaymentMethodId = (
  paymentMethod: SelectedPaymentMethod | null | undefined
): string | null => {
  if (!paymentMethod) {
    return null;
  }

  // Check for PaymentOption ID first (optionId)
  if (paymentMethod.optionId) {
    return paymentMethod.optionId;
  }

  // Check for Card ID (cardId)
  if (paymentMethod.cardId) {
    return paymentMethod.cardId;
  }

  // Check for CompanyBenefit ID (benefitId)
  if (paymentMethod.uid) {
    return paymentMethod.uid;
  }

  // Check for ExternalPaymentWrapper ID (if it has a specific ID field)
  if (paymentMethod.externalPaymentType) {
    return paymentMethod.externalPaymentType;
  }

  return null;
};

// Utility function to compare two payment methods
export const arePaymentMethodsEqual = (
  method1: SelectedPaymentMethod | null | undefined,
  method2: SelectedPaymentMethod | null | undefined
): boolean => {
  const id1 = getPaymentMethodId(method1);
  const id2 = getPaymentMethodId(method2);

  return id1 === id2;
};

export const getPaymentMethodLabel = (
  paymentType: string,
  t: (key: string) => string
): string => {
  switch (paymentType) {
    case 'MOBILEPAY':
      return t('paymentMethods.mobilePay');
    case 'SWISH':
      return t('paymentMethods.swish');
    default:
      return '';
  }
};

export const canOpenExternalPaymentApp = async () => {
  // Currently we only have Swish app external payment method
  return await Linking.canOpenURL('swish://');
};

const PAYMENT_APP_URLS: Partial<Record<PaymentType, string>> = {
  SWISH: 'swish://',
  MOBILEPAY: 'mobilepay://',
};

export const openPaymentApp = async (
  paymentType: Maybe<PaymentType>,
  paymentMethodName: Maybe<string>,
  t: (key: string, options?: object) => string
): Promise<boolean> => {
  if (!paymentType) return false;
  const url = PAYMENT_APP_URLS[paymentType];
  if (!url) return false;
  try {
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      return true;
    }
    showToast(
      t('parking.parkingProcess.unableOpenExternalApp', {
        provider: paymentMethodName,
      }),
      'error'
    );
  } catch {
    showToast(
      t('parking.parkingProcess.externalAppNotInstalled', {
        provider: paymentMethodName,
      }),
      'error'
    );
  }
  return false;
};

export const getCAbenefit = (
  paymentMethod: SelectedPaymentMethod | undefined
) => {
  if (isCompanyBenefit(paymentMethod)) {
    return paymentMethod?.benefitTargetUid;
  }
  if (isCorporateAccountPaymentOption(paymentMethod)) {
    return (paymentMethod as any)?.optionId;
  }
  return undefined;
};

export const getDefaultPaymentOption = (
  paymentOptions: Maybe<PaymentOption[]> | undefined,
  paymentCards: Maybe<Card[]> | undefined
): { paymentOption: PaymentOption; card?: Card } | null => {
  const defaultOption =
    paymentOptions?.find(
      (option) => option?.isDefaultTokenizedPaymentOption === true
    ) ?? null;
  if (!defaultOption) return null;

  const card =
    defaultOption.method === 'CREDIT_CARD'
      ? (paymentCards?.find((c) => c?.cardId === defaultOption.optionId) ??
        undefined)
      : undefined;

  return { paymentOption: defaultOption, card };
};

export const getPaymentMethodProps = ({
  session,
  receiptData,
  chargingPaymentType,
  transactionId,
  totalAmount,
}: {
  session?: Maybe<SessionWithPayment>;
  receiptData?: Maybe<Receipt>;
  chargingPaymentType?: Maybe<PaymentType>;
  transactionId?: Maybe<string>;
  totalAmount?: Maybe<number>;
}) => {
  const billingItem = getCorrectBillingItem(
    receiptData?.items as BillingItem[]
  )?.[0];

  const cardId = session?.cardId || receiptData?.creditCard?.cardId;

  const corporateAccountUid = session?.corporateAccountUid;

  const resolvedTotalAmount = totalAmount ?? billingItem?.amountCents;

  const resolvedPaymentType =
    session?.paymentType ??
    getChargingSessionPaymentType(
      cardId,
      corporateAccountUid,
      chargingPaymentType
    );

  const otherPaymentProvider =
    receiptData?.paymentProvider === 'SWISH' ? 'SWISH' : null;

  return {
    transactionId,
    cardId,
    corporateAccountUid,
    totalAmount: resolvedTotalAmount,
    otherPaymentProvider,
    paymentType: resolvedPaymentType,
  };
};
